import React from 'react';
import PropTypes from 'prop-types';

import {Box, Button, Typography} from 'views/components';
import {ArrowForwardIos, Check} from 'views/icons';
import {Translate} from 'views/module';

const ScheduleEventAdd = (props) => {
  const {onAddClick, selectedOptionId, onSelectedOptionIdChange} = props;

  const scheduleOptions = React.useMemo(() => ([
    {
      id: 'RECURRING',
      titleExpression: 'schedulestep.ScheduleEventAdd.RecurringTitle',
      descriptionExpression: 'schedulestep.ScheduleEventAdd.RecurringDescription',
    },
    {
      id: 'FLEXIBLE',
      titleExpression: 'schedulestep.ScheduleEventAdd.FlexibleTitle',
      descriptionExpression: 'schedulestep.ScheduleEventAdd.FlexibleDescription',
    },
    {
      id: 'ON_CALL',
      titleExpression: 'schedulestep.ScheduleEventAdd.OnCallTitle',
      descriptionExpression: 'schedulestep.ScheduleEventAdd.OnCallDescription',
    },
  ]), []);
  const [localSelectedOptionId, setLocalSelectedOptionId] = React.useState('RECURRING');
  const activeSelectedOptionId = selectedOptionId || localSelectedOptionId;

  const handleOptionSelect = React.useCallback((optionId) => {
    setLocalSelectedOptionId(optionId);
    if (onSelectedOptionIdChange) {
      onSelectedOptionIdChange(optionId);
    }
  }, [onSelectedOptionIdChange]);

  const handleContinue = React.useCallback(() => {
    if (onAddClick) {
      onAddClick(activeSelectedOptionId);
    }
  }, [activeSelectedOptionId, onAddClick]);

  return (
    <Box
      sx={{
        border: '1px dashed #C5BBB9',
        borderRadius: '24px',
        p: '32px',
        backgroundColor: 'white',
        display: 'flex',
        flexDirection: 'column',
        gap: '32px',
      }}
    >
      <Box sx={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between'}}>
        <Box sx={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
          <Typography sx={{fontSize: '20px', fontWeight: 300, color: '#2D2925', lineHeight: '128%', fontFamily: '"Spectral", serif'}}>
            <Translate expression={'schedulestep.ScheduleEventAdd.Title'} />
          </Typography>
          <Typography sx={{fontSize: '16px', fontWeight: 400, color: '#2D2925A3', lineHeight: '128%', maxWidth: '300px'}}>
            <Translate expression={'schedulestep.ScheduleEventAdd.Description'} />
          </Typography>
        </Box>

        <Box sx={{border: '1px solid #C5BBB9', borderRadius: '22px', px: '16px', py: '8px'}}>
          <Typography sx={{fontSize: '14px', fontWeight: 400, color: '#3C3C3C', lineHeight: '128%', whiteSpace: 'nowrap'}}>
            <Translate expression={'schedulestep.ScheduleEventAdd.StepLabel'} />
          </Typography>
        </Box>
      </Box>

      <Box sx={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
        {
          scheduleOptions.map((scheduleOption) => {
            const isSelected = activeSelectedOptionId === scheduleOption.id;

            return (
              <Box
                key={scheduleOption.id}
                onClick={() => handleOptionSelect(scheduleOption.id)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleOptionSelect(scheduleOption.id);
                  }
                }}
                role="button"
                aria-pressed={isSelected}
                tabIndex={0}
                sx={{
                  borderRadius: '12px',
                  border: isSelected ? '1px solid #DA7955' : '1px solid #DED8D7',
                  backgroundColor: isSelected ? '#FFFDFC' : '#FFFFFF',
                  p: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '16px',
                  cursor: 'pointer',
                }}
              >
                <Box sx={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
                  <Typography sx={{fontSize: '16px', fontWeight: 500, color: '#2D2925', lineHeight: '150%'}}>
                    <Translate expression={scheduleOption.titleExpression} />
                  </Typography>
                  <Typography sx={{fontSize: '16px', fontWeight: 400, color: '#696969', lineHeight: '150%', maxWidth: '320px'}}>
                    <Translate expression={scheduleOption.descriptionExpression} />
                  </Typography>
                </Box>

                <Box
                  sx={{
                    width: '28px',
                    height: '28px',
                    minWidth: '28px',
                    borderRadius: '50%',
                    border: isSelected ? 'none' : '1px solid #A69E9A',
                    backgroundColor: isSelected ? '#F3E4DD' : '#FFFFFF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {
                    isSelected &&
                    <Check sx={{fontSize: 16, color: '#DA7955'}} />
                  }
                </Box>
              </Box>
            );
          })
        }
      </Box>

      <Box sx={{display: 'flex', justifyContent: 'flex-end'}}>
        <Button
          onClick={handleContinue}
          variant='contained'
          endIcon={<ArrowForwardIos />}
          sx={{
            'borderRadius': '12px',
            'textTransform': 'none',
            'backgroundColor': '#DA7955',
            'fontSize': '16px',
            'fontWeight': 500,
            'lineHeight': '150%',
            'color': '#F6F2EF',
            'px': '20px',
            'py': '14px',
            'gap': '4px',
            '&:hover': {backgroundColor: '#C86945'},
          }}
        >
          <Translate expression={'schedulestep.ScheduleEventAdd.ContinueButton'} />
        </Button>
      </Box>
    </Box>
  );
};

ScheduleEventAdd.propTypes = {
  onAddClick: PropTypes.func,
  selectedOptionId: PropTypes.string,
  onSelectedOptionIdChange: PropTypes.func,
};

export default React.memo(ScheduleEventAdd);
